import { useTrackImpressionEventByIntersection } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import { type FunctionComponent, useCallback } from "react";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import type { Event } from "~/components/tracking/google-analytics/types";
import type { Event as Ga4Event } from "~/components/tracking/google-analytics-4/types";
import { rosettaAtom } from "~/lib/rosetta";

import { HomeFooterDownloadAppWidget } from "./download-app-widget";
import { HomeFooterNewsletter } from "./newsletter";
import {
  Container,
  Content,
  Copyright,
  Header,
  HomeFooterDownloadAppWidgetContainer,
  HomeFooterNewsletterContainer,
  HomeFooterSubscriptionWidgetContainer,
  StyledHomeFooterLogo,
  StyledHomeFooterSitemap,
  StyledHomeFooterSocialMedia,
  Wrapper,
} from "./styles";
import { HomeFooterSubscriptionWidget } from "./subscription-widget";

export type Props = {
  className?: string;
};

export const HomeFooter: FunctionComponent<Props> = ({ className }) => {
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const isScmpSubscriber = asyncRosettaState?.result?.user?.isSCMPSubscriber ?? false;
  const subscriberPhase = asyncRosettaState?.result?.tracking?.ga4.subscriberPhase;

  const impressionEvent = useCallback<() => Event>(
    () => ({
      action: "Impression/footer",
      category: "Homepage Depth",
    }),
    [],
  );

  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection<
    Event,
    null
  >({
    gaTrackingHandler: sendGATracking,
    getEvent: impressionEvent,
    options: { isSendGATracking: true, shouldSendOnce: true },
  });

  const subscribeButtonImpressionUaEvent = useCallback<() => Event>(
    () => ({
      action: "Subscription Entry Point/Footer/Impression",
      category: "Subscription Entry Point",
    }),
    [],
  );
  const subscribeButtonImpressionGA4Event = useCallback<() => Ga4Event>(
    () => ({
      action: "imp",
      category: "subs",
      customized_parameters: {
        cta_type: "subscribe",
        subscriber_phase: subscriberPhase,
        trigger_point: "homepage footer",
      },
      subcategory: "entry_point",
    }),
    [subscriberPhase],
  );
  const { captureTrackImpressionEventTargetElement: captureSubscribeButtonImpressionEvent } =
    useTrackImpressionEventByIntersection({
      gaTrackingHandler: null,
      getEvent: subscribeButtonImpressionUaEvent,
      getGa4Event: subscribeButtonImpressionGA4Event,
      options: { isSendGA4Tracking: true, isSendGATracking: true, shouldSendOnce: true },
    });

  const handleClick = useCallback((destination: string) => {
    sendGATracking({
      action: "Click/footer",
      category: "Homepage Depth",
      label: destination,
    });
  }, []);

  return (
    <Container className={className} ref={captureTrackImpressionEventTargetElement}>
      <Wrapper>
        <Header>
          <StyledHomeFooterLogo onClick={handleClick} />
          <StyledHomeFooterSocialMedia onClick={handleClick} />
        </Header>
        <Content $isScmpSubscriber={isScmpSubscriber}>
          <StyledHomeFooterSitemap onClick={handleClick} />
          {!isScmpSubscriber && (
            <HomeFooterSubscriptionWidgetContainer ref={captureSubscribeButtonImpressionEvent}>
              <HomeFooterSubscriptionWidget onClick={handleClick} />
            </HomeFooterSubscriptionWidgetContainer>
          )}
          <HomeFooterNewsletterContainer>
            <HomeFooterNewsletter onClick={handleClick} />
          </HomeFooterNewsletterContainer>
          <HomeFooterDownloadAppWidgetContainer>
            <HomeFooterDownloadAppWidget onClick={handleClick} />
          </HomeFooterDownloadAppWidgetContainer>
        </Content>
        <Copyright>
          Copyright © {new Date().getFullYear()} South China Morning Post Publishers Ltd. All rights
          reserved.
        </Copyright>
      </Wrapper>
    </Container>
  );
};

HomeFooter.displayName = "HomeFooter";
